import React, { useState } from 'react';
import { Input, Form, Row, Col, Checkbox, Modal, Button, Tooltip, Spin, notification } from 'antd';
import { Setting } from '../../../shared/types';
import { handleFormChange, validateMessages } from '../../../shared/Utils';
import { useCreateSettingMutation } from '../../../GraphQL/hooks/useCreateSettingMutation';
import { RedoOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';

const SettingCreateModal = ({ isOpen, onRequestClose, updateList }) => {
    const [form] = Form.useForm();
    const [formError, setFormError] = useState(false);
    const { mutation, loading: creating } = useCreateSettingMutation();

    const handleCommit = async () => {
        try {
            const values = await form.validateFields();
            if (values) {
                await mutation({ ...values })
                    .then(() => {
                        onRequestClose(false);
                        updateList();
                    })
                    .finally(() => {
                        notification.success({
                            message: 'Setting created successfully',
                            description: 'The setting has been created successfully.',
                        });
                    })
                    .catch((err) => {
                        notification.error({
                            message: 'Failed to create setting',
                            description: 'An error occurred while creating the setting.',
                        });

                        console.error('Failed to update pricing:', err);
                    });
            } else {
                notification.error({
                    message: 'Form validation failed',
                    description: 'Please check the form for errors and try again.',
                });
            }
        } catch (errorInfo) {
            notification.error({
                message: 'Form validation failed',
                description: 'Please check the form for errors and try again.',
            });
            console.error('Form validation failed:', errorInfo);
        }
    };

    const generatePassword = () => {
        const newPassword = uuidv4().slice(0, 10);
        form.setFieldsValue({ password: newPassword });
    };

    const handleCancel = () => {
        onRequestClose(false);
        form.resetFields();
    };

    return (
        <Modal
            title="Add user setting"
            width={750}
            open={isOpen}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button key="create" type="primary" onClick={handleCommit} disabled={formError}>
                    Create
                </Button>,
            ]}
        >
            <Spin spinning={creating}>
                <Form<Setting>
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 24 }}
                    form={form}
                    name="SettingCreate"
                    onFieldsChange={() => {
                        setFormError(handleFormChange(form));
                    }}
                    validateMessages={validateMessages}
                >
                    <Row gutter={[0, 8]}>
                        <Col span={12}>
                            <Form.Item
                                name={['fullname']}
                                label="Fullname"
                                rules={[{ required: true }]}
                                style={{ display: 'inline-block', width: '90%' }}
                            >
                                <Input type="text" placeholder={'User fullname'} />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                name={['email']}
                                label="Email"
                                rules={[{ required: true }]}
                                style={{ display: 'inline-block', width: '90%' }}
                            >
                                <Input type="email" autoComplete="new-email" placeholder={'Email user'} />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                name={['password']}
                                label="Password"
                                rules={[{ required: true }]}
                                style={{ display: 'inline-block', width: '90%' }}
                            >
                                <Input.Password
                                    type="password"
                                    autoComplete="new-password"
                                    placeholder="Generate password"
                                    addonAfter={
                                        <Tooltip title="Generate new password" color={'#108ee9'}>
                                            <RedoOutlined style={{ color: '#1677ff' }} onClick={generatePassword} />
                                        </Tooltip>
                                    }
                                />
                            </Form.Item>
                        </Col>

                        <Col span={11}>
                            <Form.Item
                                label="Roles"
                                name={['sectionRoles']}
                                rules={[{ required: true }]}
                                style={{ display: 'inline-block', width: '100%' }}
                            >
                                <Checkbox.Group>
                                    <Row gutter={[32, 8]}>
                                        <Col span={10}>
                                            <Checkbox value="Reports">Reports</Checkbox>
                                        </Col>
                                        <Col span={10}>
                                            <Checkbox value="Map">Map</Checkbox>
                                        </Col>
                                        <Col span={10}>
                                            <Checkbox value="MapTaxi">MapTaxi</Checkbox>
                                        </Col>
                                        <Col span={10}>
                                            <Checkbox value="Fleet">Fleet</Checkbox>
                                        </Col>
                                        <Col span={10}>
                                            <Checkbox value="Riders">Riders</Checkbox>
                                        </Col>
                                        <Col span={10}>
                                            <Checkbox value="TripsMicro">Trips Micro</Checkbox>
                                        </Col>
                                        <Col span={10}>
                                            <Checkbox value="TripsTaxi">Trips Taxi</Checkbox>
                                        </Col>
                                        <Col span={8}>
                                            <Checkbox value="Promos">Promos</Checkbox>
                                        </Col>
                                        <Col span={10}>
                                            <Checkbox value="Operators">Operators</Checkbox>
                                        </Col>
                                        <Col span={10}>
                                            <Checkbox value="Bookings">Bookings</Checkbox>
                                        </Col>
                                        <Col span={10}>
                                            <Checkbox value="Pricing">Pricings</Checkbox>
                                        </Col>
                                        <Col span={10}>
                                            <Checkbox value="Passes">Passes</Checkbox>
                                        </Col>
                                        <Col span={10}>
                                            <Checkbox value="Settings">Settings</Checkbox>
                                        </Col>
                                        <Col span={10}>
                                            <Checkbox value="Support">Support</Checkbox>
                                        </Col>
                                        <Col span={16}>
                                            <Checkbox value="Campaign">Campaign</Checkbox>
                                        </Col>
                                        <Col span={16}>
                                            <Checkbox value="FleetProblem">Fleet Problem</Checkbox>
                                        </Col>
                                        <Col span={16}>
                                            <Checkbox value="FinancialOverview">Financial Overview</Checkbox>
                                        </Col>
                                        <Col span={16}>
                                            <Checkbox value="TaxiDrivers">Taxi Drivers</Checkbox>
                                        </Col>
                                        <Col span={16}>
                                            <Checkbox value="SuperOperator">Super Operator</Checkbox>
                                        </Col>
                                    </Row>
                                </Checkbox.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </Modal>
    );
};

export default SettingCreateModal;
